import * as React from 'react';
import {useEffect, useState} from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid2 as Grid,
    TextField,
    Tooltip
} from '@mui/material';
import {Add, ArrowDropUp, Delete, Edit, FilterAltOff, KeyboardArrowDown, Save} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import subStorageService from '../../services/SubStorageService';
import {useTitle} from '../dashboard/TitleContext';
import {useSnackbar} from '../../util/SnackbarProvider';
import FilterPanel from "../common/FilterPanel";
import IconButton from "@mui/material/IconButton";
import {ArrowDropDownIcon} from "@mui/x-date-pickers";
import ConfirmationDialog from "../common/ConfirmationDialog";

const columns = [
    {id: 'name', label: 'Név'},
    {id: 'createdAt', label: 'Létrehozva'}
];

const ALL_COLUMNS = [
    {id: 'name', label: 'Név', searchable: true, editable: true},
    {id: 'createdAt', label: 'Létrehozva'}
];

const DEFAULT_COLUMNS = ['name', 'createdAt'];
const LOCAL_STORAGE_ITEM_NAME = "substorages-rows-per-page"
const LOCAL_STORAGE_SEARCH_PARAMS = "substorages-search-params"
const LOCAL_STORAGE_COLUMN_NAMES = "substorages-column-names"
const DEFAULT_SEARCH_PARAMS = {}

export default function SubStorageTable() {

    useTitle([{name: 'Alraktárak'}])

    const {sendAlertMessage} = useSnackbar()
    const navigate = useNavigate();
    const [dialogState, setDialogState] = React.useState({open: false, object: null})
    const [tableData, setTableData] = useState({
        page: 0,
        rowsPerPage: localStorage.getItem(LOCAL_STORAGE_ITEM_NAME) || 25,
        totalCount: 0
    })
    const [searchParams, setSearchParams] = useState(JSON.parse(localStorage.getItem(LOCAL_STORAGE_SEARCH_PARAMS)) || DEFAULT_SEARCH_PARAMS)
    const [columnNames, setColumnNames] = React.useState(JSON.parse(localStorage.getItem(LOCAL_STORAGE_COLUMN_NAMES)) || DEFAULT_COLUMNS)
    const [expanded, setExpanded] = useState(true);
    const handleDeletionClick = (object) => {
        if (object.id == null) retrieveSubStorages()
        else setDialogState({open: true, object: object})
    }
    const handleClose = () => setDialogState({open: false, object: null})
    const [indexOfEditedRow, setIndexOfEditedRow] = React.useState();
    const filterPanelRef = React.useRef();

    useEffect(() => {
        retrieveSubStorages()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData.page, tableData.rowsPerPage, searchParams])

    const retrieveSubStorages = () => {
        subStorageService.search(buildQueryParams())
            .then(response => setTableData((prev) => ({
                ...prev,
                totalCount: response.totalElements,
                rows: response.content
            })))
            .catch(error => sendAlertMessage(error?.message || "Alraktárak lekérése sikertelen", 'error'))
    }

    function buildQueryParams() {
        return {
            page: tableData.page,
            size: tableData.rowsPerPage,
            ...(searchParams?.name && {name: searchParams.name})
        }
    }

    const deleteSubStorage = (id) => {
        subStorageService.deleteSubStorage(id)
            .then(() => {
                sendAlertMessage('Alraktár sikeresen törölve')
                retrieveSubStorages()
            })
            .catch(error => sendAlertMessage(error?.message || 'Alraktár törlése sikertelen', 'error'))
    }

    const onSearchParamsChange = (event, n, v) => {
        let name = event?.target?.name || n
        let value = event?.target?.value || v
        setSearchParams((prev) => ({
            ...prev,
            [name]: value
        }))
    }

    const clear = () => {
        if (searchParams != null) sendAlertMessage('Szűrők alaphelyzetbe állítva', 'info')
        setSearchParams(DEFAULT_SEARCH_PARAMS)
        filterPanelRef.current?.resetSelection();
    }

    useEffect(() => {
        if (searchParams) localStorage.setItem(LOCAL_STORAGE_SEARCH_PARAMS, JSON.stringify(searchParams));
        else localStorage.removeItem(LOCAL_STORAGE_SEARCH_PARAMS)
    }, [searchParams]);

    const editOrSaveClick = (e, index) => {
        if (indexOfEditedRow != null) {
            persistSubStorage(e, index)
            setIndexOfEditedRow(null)
        } else setIndexOfEditedRow(index)
    }

    const iconButtonStyle = {
        position: "relative",
        top: "0px",
        left: "0px",
        width: "24px",
        color: "#808080",
        cursor: "pointer"
    }

    const onChangeColumnCheckBox = (event) => {
        const {checked, name} = event.target;
        setColumnNames((prev) => {
            return checked ? prev.includes(name) ? prev : [...prev, name] : prev.filter((col) => col !== name);
        });
    };

    useEffect(() => {
        if (columnNames) localStorage.setItem(LOCAL_STORAGE_COLUMN_NAMES, JSON.stringify(columnNames));
        else localStorage.removeItem(LOCAL_STORAGE_COLUMN_NAMES)
    }, [columnNames]);

    const addEmptyRow = () => {
        setTableData(prev => ({
            ...prev,
            rows: [{}, ...prev.rows]
        }));
        setIndexOfEditedRow(0)
    };

    function onChangeTaskType(e, index) {
        const {name, value} = e.target;

        setTableData(prev => {
            const newRows = [...prev.rows];
            newRows[index] = {...newRows[index], [name]: value};

            return {
                ...prev,
                rows: newRows
            };
        });
    }

    const persistSubStorage = (e, index) => {
        e.preventDefault();
        const subStorage = tableData.rows[index]
        if (subStorage.id)
            subStorageService.updateSubStorage(subStorage.id, subStorage)
                .then(() => {
                    sendAlertMessage('Alraktár sikeresen módosítva')
                    retrieveSubStorages()
                })
                .catch(error => sendAlertMessage(error?.message || 'Alraktár módosítása sikertelen', 'error'))
        else subStorageService.createSubStorage(subStorage)
            .then(() => {
                sendAlertMessage('Alraktár sikeresen hozzáadva')
            })
            .catch(error => sendAlertMessage(error?.message || 'Alraktár mentése sikertelen', 'error'))
            .finally(() => retrieveSubStorages())
    }

    return (
        <>
            <Accordion style={{marginBottom: "10px"}} disableGutters expanded={expanded}
                       onChange={() => setExpanded(!expanded)}>
                <AccordionSummary
                    expandIcon={<KeyboardArrowDown sx={{marginRight: "0px", height: "2em", width: "auto"}}/>} sx={{
                    minHeight: "0px",
                    fontSize: "24px",
                    fontWeight: "bold",
                    backgroundColor: "darkgray",
                    borderRadius: "4px 4px 0px 0px",
                    paddingLeft: "20px",
                    margin: "0"
                }}>
                    Keresési Feltételek
                </AccordionSummary>
                <AccordionDetails className="filter-panel">
                    <Grid container columns={10} spacing={2} padding={"10px"}>
                        <Grid size={{xs: 7}}>
                            <Grid container columns={20} spacing={2} padding={"10px"}>
                                <Grid size={{xs: 20}}>
                                    {ALL_COLUMNS.map((item, index) => (
                                        <FormControlLabel key={index}
                                                          control={<Checkbox
                                                              variant="outlined"
                                                              margin="normal"
                                                              size="large"
                                                              name={item.id}
                                                              onChange={onChangeColumnCheckBox}
                                                              checked={columnNames?.includes(item.id) || false}
                                                              sx={{padding: "0px"}}
                                                          />} label={item.label?.props?.title || item.label}
                                                          style={{paddingRight: "20px", margin: "0px"}}/>
                                    ))}
                                </Grid>

                            </Grid>
                        </Grid>
                        <Grid size={{xs: 3}}>
                            <FilterPanel
                                type="SUB_STORAGE"
                                ref={filterPanelRef}
                                currentSearchParams={searchParams}
                                onFilterSelect={(params) => setSearchParams(params)}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>

            <Paper sx={{width: '100%', overflow: 'hidden', fontSize: '10px'}}>
                <TableContainer>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell key="actions"
                                           style={{width: "100px", paddingLeft: "10px"}}
                                           align='left' padding='none'>
                                    <Tooltip title="Új hozzáadása">
                                        <Button variant='contained' color='success'
                                                sx={{minWidth: "0px", padding: "0px", marginRight: "10px"}}
                                                onClick={() => addEmptyRow()}>
                                            <Add fontSize="large"/>
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Szűrők törlése">
                                        <Button variant='contained' color='info'
                                                sx={{minWidth: "0px", padding: "0px", marginRight: "10px"}}
                                                onClick={clear}>
                                            <FilterAltOff fontSize="large"/>
                                        </Button>
                                    </Tooltip>
                                </TableCell>
                                {ALL_COLUMNS.filter(column => columnNames.includes(column.id)).map((column, index) => (
                                    <TableCell key={index} sx={{padding: "8px"}}>
                                        {column.searchable ? <TextField name={column.id} label={column.label} size="small"
                                                                        value={searchParams[column.id] || ""} onChange={onSearchParamsChange}
                                                                        sx={{margin: "0px", width: "calc(100% - 40px)"}} autoComplete="off"/> : column.label}
                                        <IconButton onClick={() => setSearchParams(prev => ({
                                            ...prev,
                                            orderBy: column.id,
                                            sort: searchParams.sort === 'ASC' ? 'DESC' : 'ASC'
                                        }))}>
                                            {searchParams.sort == null || searchParams.orderBy !== column.id ?
                                                <ArrowDropDownIcon sx={{color: "lightgray"}}/> :
                                                searchParams.sort === 'ASC' ? <ArrowDropDownIcon/> : <ArrowDropUp/>}
                                        </IconButton>
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableData.rows == null ?
                                <TableRow sx={{textAlign: "center"}}>
                                    <TableCell colSpan={columnNames?.length || 8}
                                               sx={{padding: "200px !important", textAlign: "center"}}>
                                        <CircularProgress/>
                                    </TableCell>
                                </TableRow> :
                                <>
                                    {tableData.rows?.map((row, index) => (
                                        <TableRow key={index} hover>
                                            <TableCell padding='none' align='center' sx={{lineHeight: "0px"}}>
                                                <div style={{
                                                    display: "inline-flex",
                                                    padding: "2px",
                                                    width: "100%",
                                                    height: "30px !important",
                                                }}>
                                                    <Tooltip
                                                        title={indexOfEditedRow === index ? "Mentés" : "Szerkesztés"}
                                                        placement="left">
                                                        {indexOfEditedRow === index ?
                                                            <Save onClick={e => editOrSaveClick(e, index)}
                                                                  sx={iconButtonStyle}/> :
                                                            <Edit onClick={e => editOrSaveClick(e, index)}
                                                                  sx={iconButtonStyle}/>
                                                        }
                                                    </Tooltip>
                                                    <Tooltip title={"Törlés"} placement="right">
                                                        <Delete onClick={() => handleDeletionClick(row)} sx={{
                                                            position: "relative",
                                                            top: "0px",
                                                            left: "0px",
                                                            width: "24px",
                                                            color: "#808080",
                                                            cursor: "pointer"
                                                        }}/>
                                                    </Tooltip>
                                                </div>
                                            </TableCell>

                                            {ALL_COLUMNS.filter(c => columnNames.includes(c.id)).map((column, columnIndex) => {
                                                let value = row[column.id]
                                                let valueToShow = column.fun ? column.fun(value) : value
                                                return (<TableCell key={columnIndex} align={column.align} sx={{
                                                    padding: "0px 10px",
                                                    '& input': {fontSize: "0.8rem !important", padding: "0px"}
                                                }}>
                                                    {(indexOfEditedRow === index && column.editable) ?
                                                            <TextField name={column.id}
                                                                       variant='standard' required={column.required}
                                                                       onChange={e => onChangeTaskType(e, index)}
                                                                       value={value || ''}
                                                                       type={column.type || "text"}>
                                                            </TextField>
                                                        : (valueToShow == null ? "" : valueToShow)}
                                                </TableCell>)
                                            })}

                                        </TableRow>
                                    ))}
                                </>}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[3, 10, 25, 50]}
                    component="div"
                    count={tableData.totalCount}
                    rowsPerPage={parseInt(tableData.rowsPerPage)}
                    page={tableData.page}
                    onPageChange={(event, newPage) => setTableData((prev) => ({...prev, page: newPage}))}
                    onRowsPerPageChange={(event) => {
                        setTableData((prev) => ({...prev, rowsPerPage: event.target.value, page: 0}))
                        localStorage.setItem(LOCAL_STORAGE_ITEM_NAME, event.target.value)
                    }}
                    labelRowsPerPage="Elemek oldalanként:"
                />
            </Paper>

            <ConfirmationDialog
                title={"Biztosan törölni szeretnéd ezt az alraktárt?"}
                content={
                    <>
                        {dialogState.object?.name} <br/>
                    </>
                }
                open={dialogState.open}
                onClose={handleClose}
                onConfirm={() => {
                    deleteSubStorage(dialogState.object?.id)
                }}
            />
        </>
    )
}
